/**
 * Global audit-log timeline (Phase 13 Cluster A — Feature D).
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { EmptyState, GlassPanel, LoadingSpinner, PageHeader, Pagination } from '../components/ui';
import { AuditTimeline } from '../components/audit/AuditTimeline';
import {
  AUDIT_ACTION_LABELS,
  AUDIT_ENTITY_LABELS,
  useAuditList,
} from '../hooks/useAuditLog';
import type { AuditAction, AuditEntityType } from '@armard/shared';

const auditSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  entityType: z
    .enum([
      'user',
      'customer',
      'address',
      'invoice',
      'invoice_item',
      'payment_reminder',
      'contract',
      'contract_item',
      'cost_estimate',
      'estimate_item',
      'settings',
    ])
    .optional(),
  action: z.enum(['create', 'update', 'delete']).optional(),
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

type AuditSearch = z.infer<typeof auditSearchSchema>;

export const Route = createFileRoute('/audit-log')({
  component: AuditLogPage,
  validateSearch: auditSearchSchema,
});

function AuditLogPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  const { data, isLoading, error } = useAuditList({
    page: search.page,
    limit: 40,
    entityType: search.entityType,
    action: search.action,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
  });

  const updateSearch = (patch: Partial<AuditSearch>) => {
    navigate({
      search: (prev: AuditSearch) => ({ ...prev, ...patch, page: 1 }),
      replace: true,
    });
  };

  const hasActiveFilter = !!(search.entityType || search.action || search.dateFrom || search.dateTo);

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header — 006b P3: PageHeader + Filter in der GlassPanel-Zeile (Kanon §1) */}
        <PageHeader
          title="Audit-Protokoll"
          subtitle={`${data?.total ?? 0} Einträge${hasActiveFilter ? ' (gefiltert)' : ''}`}
        />

        <GlassPanel className="mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={search.entityType ?? ''}
              onChange={(e) =>
                updateSearch({
                  entityType: (e.target.value || undefined) as AuditEntityType | undefined,
                })
              }
              aria-label="Nach Typ filtern"
              className="bg-white/5 border border-white/10 rounded-lg px-3 h-9 text-sm text-white/80 focus:outline-none focus:ring-2 focus:ring-white/20"
            >
              <option value="">Alle Typen</option>
              {Object.entries(AUDIT_ENTITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={search.action ?? ''}
              onChange={(e) =>
                updateSearch({ action: (e.target.value || undefined) as AuditAction | undefined })
              }
              aria-label="Nach Aktion filtern"
              className="bg-white/5 border border-white/10 rounded-lg px-3 h-9 text-sm text-white/80 focus:outline-none focus:ring-2 focus:ring-white/20"
            >
              <option value="">Alle Aktionen</option>
              {Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <label className="text-sm text-white/60 inline-flex items-center gap-2">
              Von
              <input
                type="date"
                value={search.dateFrom ?? ''}
                onChange={(e) => updateSearch({ dateFrom: e.target.value || undefined })}
                className="bg-white/5 border border-white/10 rounded-lg px-2 h-9 text-sm text-white/80 focus:outline-none focus:ring-2 focus:ring-white/20"
              />
            </label>
            <label className="text-sm text-white/60 inline-flex items-center gap-2">
              Bis
              <input
                type="date"
                value={search.dateTo ?? ''}
                onChange={(e) => updateSearch({ dateTo: e.target.value || undefined })}
                className="bg-white/5 border border-white/10 rounded-lg px-2 h-9 text-sm text-white/80 focus:outline-none focus:ring-2 focus:ring-white/20"
              />
            </label>

            {hasActiveFilter && (
              <button
                onClick={() =>
                  navigate({
                    search: () => ({ page: 1 }),
                    replace: true,
                  })
                }
                className="text-sm text-white/50 hover:text-white transition-colors"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
        </GlassPanel>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">Fehler beim Laden des Audit-Protokolls</p>
          </div>
        ) : (data?.entries.length ?? 0) === 0 ? (
          <EmptyState
            title={hasActiveFilter ? 'Keine Treffer' : 'Keine Einträge'}
            description={
              hasActiveFilter
                ? 'Passen Sie die Filter an, um weitere Einträge zu sehen.'
                : 'Es wurden noch keine Änderungen protokolliert.'
            }
          />
        ) : (
          <>
            {/* 006b P4: einzige unboxte Liste der App — jetzt Kanon-Surface */}
            <GlassPanel variant="subtle" padding="sm" className="overflow-hidden mb-6">
              <AuditTimeline entries={data?.entries ?? []} showEntityLink />
            </GlassPanel>
            {data && data.totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <Pagination
                  page={search.page ?? 1}
                  totalPages={data.totalPages}
                  onPageChange={(page) =>
                    navigate({
                      search: (prev: AuditSearch) => ({ ...prev, page }),
                      replace: true,
                    })
                  }
                />
              </div>
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
